import { useState, useEffect, useRef, useCallback, Fragment } from "react";
import {
  Search,
  ChevronDown,
  ChevronUp,
  Database,
  GitBranch,
  Layers,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Unlink,
  X,
  Info,
  RefreshCw,
  Shield,
  Award,
  ArrowRight,
  Plus,
  Zap,
  Target,
  Trash2,
} from "lucide-react";
import type {
  MdmCluster,
  MdmClusterDetail,
  MdmAttributeProvenance,
  MdmLinkedRecord,
  MdmClusterHistoryEntry,
  MdmLineageNode,
  MdmRun,
  MdmRunStats,
  MdmFieldMapping,
  ConfidenceBand,
  EntityType,
  ClusterStatus,
  SurvivorshipStrategy,
} from "../types";
import {
  searchGoldenRecords,
  getGoldenRecord,
  unmergeCluster,
  listRuns,
  quickMatchEntities,
  getFields,
  getRunStatus,
  configureOutputTarget,
  getOutputConfig,
  pushGoldenRecords,
} from "../api/mdm-client";
import type {
  OracleOutputConfigRequest,
  OracleOutputConfigResponse,
} from "../types";

// ─── Helpers ────────────────────────────────────────────────────────────────

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Convert a UUID to a stable 15-char "ZTDMDM000000000" short ID.
// Uses BigInt so the full 128-bit UUID is honored with no floating-point loss.
function toShortId(id: string): string {
  const hex = id.replace(/-/g, "");
  try {
    const n = BigInt("0x" + hex) % BigInt(1_000_000_000);
    return "ZTDMDM" + String(n).padStart(9, "0");
  } catch {
    return "ZTDMDM" + hex.slice(0, 9).toUpperCase();
  }
}

// Extract the best available display name from a cluster's golden attributes.
function extractDisplayName(
  attributes: Record<string, string> | undefined,
): string | null {
  if (!attributes) return null;
  const candidates = [
    attributes.full_name,
    attributes.name,
    attributes.display_name,
    attributes.person_name,
    attributes.customer_name,
    [attributes.first_name, attributes.last_name].filter(Boolean).join(" ") ||
      null,
    attributes.company_name,
    attributes.organization_name,
    // Generic fallbacks so non-person entities (provider, facility, …) still
    // get a human label: any *_name / name-ish field, then the first non-id
    // text attribute.
    ...Object.entries(attributes)
      .filter(([k]) => /name/i.test(k))
      .map(([, v]) => v),
    ...Object.entries(attributes)
      .filter(
        ([k, v]) =>
          !/(^|_)(id|nbr|number|uuid|guid|pk)($|_)|_id$/i.test(k) &&
          !k.startsWith("_") &&
          typeof v === "string" &&
          v.trim().length > 0 &&
          !/^\d+(\.\d+)?$/.test(v.trim()),
      )
      .map(([, v]) => v),
  ];
  return candidates.find((v) => v && v.trim()) ?? null;
}

function fmtDatetime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── Badge components ────────────────────────────────────────────────────────

const BAND_CONFIG: Record<
  ConfidenceBand,
  { label: string; classes: string; dot: string }
> = {
  auto_merge: {
    label: "Auto-Merge",
    classes: "ztd-badge ztd-badge-success",
    dot: "bg-brand-500",
  },
  hitl: {
    label: "Manual Review",
    classes: "ztd-badge ztd-badge-warning",
    dot: "bg-warning",
  },
  reject: {
    label: "Rejected",
    classes: "ztd-badge ztd-badge-critical",
    dot: "bg-error",
  },
};

// A confidence band ("Auto-Merge"/"Manual Review") only means something for a
// golden that actually consolidated 2+ source records. A single-source golden
// was never merged — it's simply a unique record — so labeling it "Auto-Merge"
// (the column's default band) is misleading. Show a neutral "Unique" chip
// instead, gated on the member/source count rather than the stored band.
function MergeBadge({
  band,
  sourceCount,
}: {
  band: ConfidenceBand;
  sourceCount: number;
}) {
  if (sourceCount > 1) return <ConfidenceBadge band={band} />;
  return (
    <span className="ztd-badge tracking-wide">
      <span className="w-1.5 h-1.5 rounded-full bg-[var(--overlay-strong)]" />
      Unique
    </span>
  );
}

function ConfidenceBadge({ band }: { band: ConfidenceBand }) {
  const cfg = BAND_CONFIG[band];
  return (
    <span className={`${cfg.classes} tracking-wide`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

const STATUS_CONFIG: Record<
  ClusterStatus,
  { label: string; icon: React.ReactNode; classes: string }
> = {
  active: {
    label: "Active",
    icon: <CheckCircle2 className="w-3 h-3" />,
    classes: "ztd-badge ztd-badge-success",
  },
  pending_review: {
    label: "Pending Review",
    icon: <Clock className="w-3 h-3" />,
    classes: "ztd-badge ztd-badge-warning",
  },
  unmerged: {
    label: "Unmerged",
    icon: <Unlink className="w-3 h-3" />,
    classes: "ztd-badge",
  },
};

function StatusBadge({ status }: { status: ClusterStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span className={cfg.classes}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

const ENTITY_COLORS: Record<EntityType, string> = {
  person: "bg-blue-500/15 text-blue-400 border border-blue-500/20",
  organization: "bg-violet-500/15 text-violet-400 border border-violet-500/20",
  provider: "bg-cyan-500/15 text-cyan-400 border border-cyan-500/20",
  facility: "bg-orange-500/15 text-orange-400 border border-orange-500/20",
};

function EntityBadge({ type }: { type: EntityType }) {
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider ${ENTITY_COLORS[type]}`}
    >
      {type}
    </span>
  );
}

const SURVIVORSHIP_LABELS: Record<SurvivorshipStrategy, string> = {
  most_recent: "Most Recent",
  most_complete: "Most Complete",
  source_priority: "Source Priority",
  most_frequent: "Most Frequent",
  non_null: "Non-Null",
  llm_assisted: "LLM-Assisted",
};

// ─── Linked Records Table ────────────────────────────────────────────────────

// Every contributing source record behind the survived golden record, one row
// each. Columns are the survived attributes; the cell a record *won* is
// highlighted so a steward can trace which raw record supplied each field.
function LinkedRecordsTable({
  records,
  provenance,
}: {
  records: MdmLinkedRecord[];
  provenance: MdmAttributeProvenance[];
}) {
  if (records.length === 0) {
    return (
      <p className="text-sm text-[var(--text-primary)] py-4 text-center">
        No contributing records linked to this golden record.
      </p>
    );
  }

  // Column order follows the survived attributes; fall back to the union of
  // whatever keys the linked records carry when provenance is unavailable.
  const columns =
    provenance.length > 0
      ? provenance.map((p) => p.attribute)
      : Array.from(
          new Set(records.flatMap((r) => Object.keys(r.attributes ?? {}))),
        );

  const goldenByAttr = new Map(provenance.map((p) => [p.attribute, p]));

  return (
    <div className="overflow-x-auto rounded-lg border border-[var(--border-subtle)]">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-white/[0.03]">
            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--text-primary)] whitespace-nowrap sticky left-0 bg-[var(--surface-card)] z-10">
              Source Record
            </th>
            {columns.map((col) => (
              <th
                key={col}
                className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--text-primary)] whitespace-nowrap font-mono"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Survived (golden) row — pinned at the top for comparison */}
          <tr className="border-t border-[#86bc25]/20 bg-[#86bc25]/[0.06]">
            <td className="px-3 py-2.5 whitespace-nowrap sticky left-0 bg-[#20261a] z-10">
              <div className="flex items-center gap-1.5">
                <Award className="w-3.5 h-3.5 text-[#86bc25]/70" />
                <span className="text-[11px] font-semibold text-[#86bc25]/90">
                  Golden Record
                </span>
              </div>
            </td>
            {columns.map((col) => (
              <td
                key={col}
                className="px-3 py-2.5 text-white/90 font-medium whitespace-nowrap"
              >
                {goldenByAttr.get(col)?.value || (
                  <span className="text-[var(--text-primary)]">—</span>
                )}
              </td>
            ))}
          </tr>

          {/* One row per contributing source record */}
          {records.map((rec) => {
            const won = new Set(rec.won_attributes ?? []);
            return (
              <tr
                key={rec.record_id}
                className="border-t border-white/[0.04] hover:bg-white/[0.015] transition-colors"
              >
                <td className="px-3 py-2.5 whitespace-nowrap sticky left-0 bg-[var(--surface-card)] z-10">
                  <div className="flex items-center gap-1.5">
                    <Database className="w-3.5 h-3.5 text-[var(--text-primary)] shrink-0" />
                    <span className="text-[12px] font-medium text-white/70">
                      {rec.source_system}
                    </span>
                    {rec.source_pk && (
                      <span className="text-[11px] font-mono text-[var(--text-primary)]">
                        {rec.source_pk}
                      </span>
                    )}
                  </div>
                </td>
                {columns.map((col) => {
                  const val = rec.attributes?.[col];
                  const isWinner = won.has(col);
                  return (
                    <td
                      key={col}
                      className={`px-3 py-2.5 whitespace-nowrap ${
                        isWinner
                          ? "bg-[#86bc25]/10 text-[#86bc25]/90 font-medium"
                          : "text-white/55"
                      }`}
                      title={
                        isWinner ? "This record won this attribute" : undefined
                      }
                    >
                      {val ? (
                        val
                      ) : (
                        <span className="text-[var(--text-primary)]">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Lineage Mini-Graph ──────────────────────────────────────────────────────

function LineageMiniGraph({
  nodes,
  clusterId,
}: {
  nodes: MdmLineageNode[];
  clusterId: string;
}) {
  const W = 520;
  const H = Math.max(100, nodes.length * 52 + 20);
  const cx = W - 100;
  const cy = H / 2;
  const srcX = 120;

  return (
    <div className="overflow-x-auto">
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        className="text-[var(--text-primary)]"
        style={{ minWidth: W }}
        aria-label="Source lineage graph"
      >
        {/* Source nodes */}
        {nodes.map((n, i) => {
          const y = 36 + i * 52;
          return (
            <Fragment key={n.source_pk}>
              {/* Connector line */}
              <line
                x1={srcX + 90}
                y1={y}
                x2={cx}
                y2={cy}
                stroke="rgba(134,188,37,0.25)"
                strokeWidth={1.5}
                strokeDasharray="4 3"
              />
              {/* Source box */}
              <rect
                x={srcX - 80}
                y={y - 20}
                width={170}
                height={40}
                rx={6}
                fill="rgba(28,28,42,0.85)"
                stroke="rgba(255,255,255,0.08)"
                strokeWidth={1}
              />
              <text
                x={srcX + 5}
                y={y - 4}
                textAnchor="middle"
                fontSize={11}
                fontWeight={600}
                fill="rgba(255,255,255,0.85)"
              >
                {n.source_system}
              </text>
              <text
                x={srcX + 5}
                y={y + 11}
                textAnchor="middle"
                fontSize={9}
                fill="rgba(255,255,255,0.4)"
              >
                {n.source_pk} · {(n.record_count ?? 1).toLocaleString()} rec
              </text>
            </Fragment>
          );
        })}

        {/* Golden Record node */}
        <rect
          x={cx - 70}
          y={cy - 28}
          width={140}
          height={56}
          rx={8}
          fill="rgba(134,188,37,0.12)"
          stroke="rgba(134,188,37,0.4)"
          strokeWidth={1.5}
        />
        <text
          x={cx}
          y={cy - 7}
          textAnchor="middle"
          fontSize={11}
          fontWeight={700}
          fill="#a7d200"
        >
          Golden Record
        </text>
        <text
          x={cx}
          y={cy + 9}
          textAnchor="middle"
          fontSize={9}
          fill="rgba(134,188,37,0.6)"
        >
          {clusterId.slice(0, 16)}…
        </text>
      </svg>
    </div>
  );
}

// ─── Change Timeline ─────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

function MdmChangeTimeline({ entries }: { entries: MdmClusterHistoryEntry[] }) {
  const [visible, setVisible] = useState(PAGE_SIZE);

  if (entries.length === 0) {
    return (
      <p className="text-sm text-[var(--text-primary)] py-4 text-center">
        No history recorded for this cluster.
      </p>
    );
  }

  const shown = entries.slice(0, visible);

  return (
    <div className="space-y-3">
      {shown.map((entry, idx) => {
        // Tolerate both history shapes: the run-diff shape and the standalone
        // service's pair-decision event shape.
        const when = entry.run_date ?? entry.timestamp;
        const label = entry.trigger_cause ?? entry.event;
        const changes = entry.changed_attributes ?? [];
        const reviewer = entry.steward_reviewer ?? entry.actor;
        const reasoning = entry.steward_reasoning ?? entry.notes;
        return (
          <div
            key={`${entry.run_id ?? entry.id ?? "entry"}-${idx}`}
            className="flex gap-3"
          >
            {/* Timeline spine */}
            <div className="flex flex-col items-center">
              <div className="w-2 h-2 rounded-full bg-[#86bc25]/60 mt-1.5 shrink-0" />
              {idx < shown.length - 1 && (
                <div className="w-px flex-1 bg-[var(--overlay-medium)] mt-1" />
              )}
            </div>

            <div className="pb-4 min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                {when && (
                  <span className="text-[11px] font-medium text-white/80">
                    {fmtDatetime(when)}
                  </span>
                )}
                {label && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-[var(--overlay-subtle)] text-[var(--text-primary)] font-mono">
                    {label}
                  </span>
                )}
                {reviewer && (
                  <span className="text-[11px] text-[var(--text-primary)]">
                    Reviewed by {reviewer}
                  </span>
                )}
              </div>

              {changes.length > 0 ? (
                <div className="space-y-1">
                  {changes.map((attr) => (
                    <div
                      key={attr.field}
                      className="flex items-center gap-2 text-[11px] font-mono"
                    >
                      <span className="text-white/50 w-28 shrink-0 truncate">
                        {attr.field}
                      </span>
                      <span className="text-red-400/70 truncate max-w-[100px]">
                        {attr.old_source}
                      </span>
                      <ArrowRight className="w-3 h-3 text-[var(--text-primary)] shrink-0" />
                      <span className="text-emerald-400/70 truncate max-w-[100px]">
                        {attr.new_source}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-[var(--text-primary)]">
                  No attribute changes recorded.
                </p>
              )}

              {reasoning && (
                <p className="mt-1.5 text-[11px] text-[var(--text-primary)] italic">
                  "{reasoning}"
                </p>
              )}
            </div>
          </div>
        );
      })}

      {visible < entries.length && (
        <button
          onClick={() => setVisible((v) => v + PAGE_SIZE)}
          className="text-xs text-[#86bc25]/70 hover:text-[#86bc25] transition-colors flex items-center gap-1 ml-5"
        >
          <RefreshCw className="w-3 h-3" />
          Load more ({entries.length - visible} remaining)
        </button>
      )}
    </div>
  );
}

// ─── Attribute Table ─────────────────────────────────────────────────────────

function AttributeRow({
  prov,
  match,
}: {
  prov: MdmAttributeProvenance;
  match?: { score: number | null; isBase: boolean };
}) {
  const [altOpen, setAltOpen] = useState(false);
  const hasAlts = prov.alternatives.length > 0;

  return (
    <>
      <tr className="border-b border-white/[0.04] hover:bg-white/[0.015] transition-colors group">
        {/* Attribute name */}
        <td className="px-4 py-2.5 text-sm font-mono text-white/70 whitespace-nowrap align-top">
          {prov.attribute}
        </td>

        {/* Value */}
        <td className="px-4 py-2.5 align-top">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-white/90 font-medium">
              {prov.value}
            </span>
            {prov.llm_assisted && (
              <span className="inline-flex items-center gap-1 text-[11px] text-[var(--text-primary)]">
                <Shield className="w-2.5 h-2.5" />
                Structural metadata only
              </span>
            )}
          </div>
        </td>

        {/* Source of record */}
        <td className="px-4 py-2.5 align-top">
          <span className="text-[11px] px-2 py-0.5 rounded bg-[#86bc25]/10 text-[#86bc25]/80 font-mono border border-[#86bc25]/15">
            {prov.winning_source}
          </span>
        </td>

        {/* Match score of the source-of-record */}
        <td className="px-4 py-2.5 align-top">
          {match?.isBase ? (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[var(--overlay-medium)] text-white/50 font-bold">
              anchor
            </span>
          ) : match?.score != null ? (
            <span
              className={`text-[11px] px-2 py-0.5 rounded-full font-bold tabular-nums ${
                match.score >= 0.95
                  ? "bg-[#2f9e44]/15 text-[#51cf66]"
                  : "bg-[#d9880a]/15 text-[#ffb84d]"
              }`}
            >
              {match.score.toFixed(2)}
            </span>
          ) : (
            <span className="text-[11px] text-[var(--text-primary)]">—</span>
          )}
        </td>

        {/* Survivorship rule */}
        <td className="px-4 py-2.5 align-top">
          <span className="text-[11px] text-[var(--text-primary)]">
            {SURVIVORSHIP_LABELS[prov.survivorship_rule]}
          </span>
        </td>

        {/* Alternatives toggle */}
        <td className="px-4 py-2.5 align-top text-right">
          {hasAlts && (
            <button
              onClick={() => setAltOpen((o) => !o)}
              className="text-[11px] text-[var(--text-primary)] hover:text-white/60 transition-colors flex items-center gap-1 ml-auto"
            >
              {prov.alternatives.length} source
              {prov.alternatives.length > 1 ? "s" : ""} provided a value
              {altOpen ? (
                <ChevronUp className="w-3 h-3" />
              ) : (
                <ChevronDown className="w-3 h-3" />
              )}
            </button>
          )}
        </td>
      </tr>

      {/* Expanded alternative sources */}
      {hasAlts && altOpen && (
        <tr className="bg-white/[0.01]">
          <td colSpan={6} className="px-4 pb-3 pt-1">
            <div className="ml-4 border-l border-[var(--border-subtle)] pl-4 space-y-1.5">
              {prov.alternatives.map((alt) => (
                <div
                  key={alt.source}
                  className="flex items-center gap-3 text-[11px]"
                >
                  <span className="text-[var(--text-primary)] font-mono w-32 shrink-0 truncate">
                    {alt.source}
                  </span>
                  <span className="text-white/50">{alt.value}</span>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function AttributeTable({
  provenance,
  linkedRecords = [],
}: {
  provenance: MdmAttributeProvenance[];
  linkedRecords?: MdmLinkedRecord[];
}) {
  // Map each attribute → the match score of the source record that won it (the
  // record whose won_attributes includes this field). The base/anchor record
  // shows "anchor" rather than a score.
  const matchByAttr = new Map<
    string,
    { score: number | null; isBase: boolean }
  >();
  for (const r of linkedRecords) {
    for (const attr of r.won_attributes ?? []) {
      if (!matchByAttr.has(attr))
        matchByAttr.set(attr, {
          score: r.match_score ?? null,
          isBase: !!r.is_base,
        });
    }
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-[var(--border-subtle)]">
      {/* LLM safety notice */}
      <div className="px-4 py-2 bg-[#86bc25]/5 border-b border-[#86bc25]/10 flex items-center gap-2">
        <Shield className="w-3.5 h-3.5 text-[#86bc25]/60 shrink-0" />
        <p className="text-[11px] text-[#86bc25]/60">
          Output layer — no values transmitted to LLM
        </p>
      </div>

      <table className="w-full text-left min-w-[540px]">
        <thead>
          <tr className="border-b border-[var(--border-subtle)] bg-white/[0.02]">
            <th className="px-4 py-2 text-[11px] font-semibold text-[var(--text-primary)] uppercase tracking-wider">
              Attribute
            </th>
            <th className="px-4 py-2 text-[11px] font-semibold text-[var(--text-primary)] uppercase tracking-wider">
              Value
            </th>
            <th className="px-4 py-2 text-[11px] font-semibold text-[var(--text-primary)] uppercase tracking-wider">
              Source of Record
            </th>
            <th className="px-4 py-2 text-[11px] font-semibold text-[var(--text-primary)] uppercase tracking-wider">
              Match
            </th>
            <th className="px-4 py-2 text-[11px] font-semibold text-[var(--text-primary)] uppercase tracking-wider">
              Rule
            </th>
            <th className="px-4 py-2 text-[11px] font-semibold text-[var(--text-primary)] uppercase tracking-wider text-right">
              Alternatives
            </th>
          </tr>
        </thead>
        <tbody>
          {provenance.map((p) => (
            <AttributeRow
              key={p.attribute}
              prov={p}
              match={matchByAttr.get(p.attribute)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Unmerge Dialog ───────────────────────────────────────────────────────────

interface UnmergeDialogProps {
  clusterId: string;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

function UnmergeDialog({ clusterId, onConfirm, onCancel }: UnmergeDialogProps) {
  const [typed, setTyped] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirmed = typed.trim() === clusterId.trim();

  const handleConfirm = async () => {
    if (!confirmed) return;
    setLoading(true);
    setError(null);
    try {
      await onConfirm();
      setSuccess(true);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Unmerge failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(10,10,18,0.85)", backdropFilter: "blur(8px)" }}
    >
      <div
        className="w-full max-w-md rounded-xl border border-red-500/25 shadow-2xl"
        style={{ background: "rgba(20,18,28,0.97)" }}
      >
        {success ? (
          <div data-testid="mdm-unmerge-success" className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-1">
              Cluster Unmerged
            </h3>
            <p className="text-sm text-white/50 mb-6">
              The cluster has been dissolved. Source records are now
              independent.
            </p>
            <button
              onClick={onCancel}
              className="px-4 py-2 rounded-lg bg-[var(--overlay-subtle)] hover:bg-[var(--overlay-strong)] text-sm text-white/70 transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[var(--text-primary)]">
                    Unmerge Cluster
                  </h3>
                  <p className="text-[11px] text-[var(--text-primary)]">
                    This action cannot be undone
                  </p>
                </div>
              </div>
              <button
                onClick={onCancel}
                className="text-[var(--text-primary)] hover:text-white/60 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Consequences */}
            <div className="mb-4 p-3 rounded-lg bg-red-500/8 border border-red-500/15">
              <p className="text-[11px] text-red-300/80 leading-relaxed">
                Unmerging this cluster will dissolve the golden record and
                return all contributing source records to an unlinked state. Any
                downstream pipelines consuming this golden record will lose
                their reference. A new MDM run will be required to re-establish
                matches.
              </p>
            </div>

            {/* Confirmation input */}
            <div className="mb-5">
              <label className="block text-[11px] font-medium text-white/50 mb-2">
                Type the cluster ID to confirm
              </label>
              <div className="relative">
                <input
                  type="text"
                  data-testid="mdm-unmerge-confirm-input"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={clusterId}
                  className="w-full px-3 py-2 rounded-lg bg-[var(--overlay-subtle)] border border-[var(--border-muted)] text-sm font-mono text-[var(--text-primary)] placeholder-white/20 focus:outline-none focus:border-red-500/40 focus:ring-1 focus:ring-red-500/20 transition-colors"
                  autoComplete="off"
                  spellCheck={false}
                />
                {confirmed && (
                  <CheckCircle2 className="absolute right-2.5 top-2.5 w-4 h-4 text-emerald-400" />
                )}
              </div>
            </div>

            {error && (
              <p className="mb-3 text-[11px] text-red-400 bg-red-500/8 rounded px-3 py-2 border border-red-500/15">
                {error}
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={onCancel}
                className="flex-1 px-4 py-2 rounded-lg bg-[var(--overlay-subtle)] hover:bg-[var(--overlay-strong)] text-sm text-white/60 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!confirmed || loading}
                data-testid="mdm-unmerge-confirm-submit"
                className="flex-1 px-4 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-sm text-red-300 font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Unmerging…
                  </>
                ) : (
                  <>
                    <Unlink className="w-3.5 h-3.5" />
                    Unmerge Cluster
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Expandable section wrapper ───────────────────────────────────────────────

function Section({
  title,
  icon,
  defaultOpen = false,
  count,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  count?: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[var(--border-subtle)] rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white/[0.025] hover:bg-white/[0.04] transition-colors"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-white/70">
          {icon}
          {title}
          {count !== undefined && (
            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-[var(--overlay-medium)] text-[var(--text-primary)] font-mono">
              {count}
            </span>
          )}
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-[var(--text-primary)]" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[var(--text-primary)]" />
        )}
      </button>
      {open && <div className="px-4 py-4">{children}</div>}
    </div>
  );
}

// ─── Detail Panel ─────────────────────────────────────────────────────────────

// The source records that matched and merged into this golden record — with
// each record's name, location, source id, and the match score that linked it.
function MatchedRecords({ records }: { records: MdmLinkedRecord[] }) {
  if (!records.length)
    return (
      <p className="text-xs text-[var(--text-primary)]">
        No linked source records for this golden record.
      </p>
    );
  const locationOf = (a: Record<string, string>) =>
    [
      a.address_line1 || a.address || "",
      [a.city, a.state, a.zip || a.zipcode].filter(Boolean).join(", "),
    ]
      .filter(Boolean)
      .join(" · ");
  return (
    <div className="space-y-2">
      {records.map((r) => {
        const name =
          extractDisplayName(r.attributes) ||
          r.source_pk ||
          r.record_id.slice(0, 8);
        const location = locationOf(r.attributes);
        const wonN = r.won_attributes?.length ?? 0;
        return (
          <div
            key={r.record_id}
            className="flex items-center gap-3 rounded-lg border border-[var(--border-muted)] bg-white/[0.03] px-3 py-2.5"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[13px] font-semibold text-white/85">
                <span className="truncate">{name}</span>
                {r.is_base && (
                  <span className="rounded bg-[#86bc25]/15 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[#a7d200]">
                    base
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-[11px] text-[var(--text-primary)]">
                {location ? `${location} · ` : ""}
                <span className="font-mono text-[var(--text-primary)]">
                  {r.source_system} {r.source_pk}
                </span>
              </div>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-3">
              {r.is_base ? (
                <span className="rounded-full bg-[var(--overlay-medium)] px-2.5 py-0.5 text-[11px] font-bold text-white/50">
                  anchor
                </span>
              ) : r.match_score != null ? (
                <span
                  className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold tabular-nums ${
                    r.match_score >= 0.95
                      ? "bg-[#2f9e44]/15 text-[#51cf66]"
                      : "bg-[#d9880a]/15 text-[#ffb84d]"
                  }`}
                >
                  {r.match_score.toFixed(2)} match
                </span>
              ) : null}
              <span className="text-[11px] font-bold tabular-nums text-[#51cf66]">
                {wonN} {wonN === 1 ? "field" : "fields"}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface DetailPanelProps {
  clusterId: string;
  onUnmerged?: () => void;
}

function MdmGoldenRecordDetailPanel({
  clusterId,
  onUnmerged,
}: DetailPanelProps) {
  const [detail, setDetail] = useState<MdmClusterDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUnmerge, setShowUnmerge] = useState(false);
  const [unmergeSucceeded, setUnmergeSucceeded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);

    getGoldenRecord(clusterId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clusterId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 text-[#86bc25]/50 animate-spin" />
          <p className="text-sm text-[var(--text-primary)]">Loading record…</p>
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3 text-center px-8">
          <AlertTriangle className="w-6 h-6 text-red-400/50" />
          <p className="text-sm text-[var(--text-primary)]">
            {error ?? "Record not found."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="px-5 py-4 border-b border-[var(--border-subtle)] shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              {detail.entity_type && <EntityBadge type={detail.entity_type} />}
              <StatusBadge status={detail.status} />
              <MergeBadge
                band={detail.origin_band}
                sourceCount={detail.source_count ?? 1}
              />
            </div>
            {(() => {
              const displayName = extractDisplayName(detail.attributes);
              return displayName ? (
                <h2
                  className="text-sm font-semibold text-white/90 truncate"
                  title={displayName}
                >
                  {displayName}
                </h2>
              ) : null;
            })()}
            <h2
              className="text-xs font-mono text-[var(--text-primary)] truncate"
              title={detail.id}
            >
              {toShortId(detail.id)}
            </h2>
            <div className="mt-1 flex items-center gap-3 text-[11px] text-[var(--text-primary)]">
              <span>
                {(() => {
                  // Reconcile the header count with the Lineage table: prefer the
                  // per-record linked_records (one row per contributing record) and
                  // also show how many distinct source systems they came from, so
                  // "N records from M sources" can't disagree with the table below.
                  // Falls back to the backend source_count when linked_records is
                  // absent (older MDM service versions).
                  const recs = detail.linked_records ?? [];
                  if (recs.length === 0)
                    return `${detail.source_count} sources`;
                  const nSources = new Set(recs.map((r) => r.source_system))
                    .size;
                  return `${recs.length} record${recs.length === 1 ? "" : "s"} from ${nSources} source${nSources === 1 ? "" : "s"}`;
                })()}
              </span>
              <span>·</span>
              <span>Updated {fmtDate(detail.last_updated)}</span>
            </div>
          </div>

          {/* Unmerge button — role-gated stub (shown to all with informational note) */}
          <button
            onClick={() => setShowUnmerge(true)}
            data-testid="mdm-unmerge-trigger"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-500/25 bg-red-500/8 text-red-400/80 hover:bg-red-500/15 hover:border-red-500/40 text-xs font-medium transition-colors shrink-0"
            title="Requires Steward or Admin role in production"
          >
            <Unlink className="w-3.5 h-3.5" />
            Unmerge
          </button>
        </div>

        {/* Role-gate note */}
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-[var(--text-primary)]">
          <Info className="w-3 h-3" />
          Unmerge action requires Steward or Admin role
        </div>
      </div>

      {/* Body — flows with the page instead of a boxed-in scroll region */}
      <div className="px-5 py-4 space-y-3">
        {/* Matched & merged records — which source records combined here */}
        <Section
          title="Matched & Merged Records"
          icon={<GitBranch className="w-4 h-4 text-[#86bc25]/60" />}
          defaultOpen
          count={detail.linked_records?.length ?? 0}
        >
          {(detail.source_count ?? 1) > 1 ? (
            <MatchedRecords records={detail.linked_records ?? []} />
          ) : (
            <p className="text-xs text-[var(--text-primary)] leading-relaxed">
              Unique record — no duplicate source records were matched to it, so
              it became a golden record on its own. Records that consolidate two
              or more sources appear under the{" "}
              <span className="text-white/60 font-medium">Merged</span> filter.
            </p>
          )}
        </Section>

        {/* Surviving attributes — value, source of record, match, rule */}
        <Section
          title="Surviving Attributes"
          icon={<Award className="w-4 h-4 text-[#86bc25]/60" />}
          defaultOpen
          count={detail.attribute_provenance.length}
        >
          <AttributeTable
            provenance={detail.attribute_provenance}
            linkedRecords={detail.linked_records ?? []}
          />
        </Section>

        {/* Lineage — linked records table + source→golden mini-graph */}
        <Section
          title="Lineage"
          icon={<GitBranch className="w-4 h-4 text-violet-400/60" />}
          defaultOpen={false}
          count={detail.linked_records?.length ?? detail.lineage.length}
        >
          <div className="space-y-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-primary)] mb-2">
                Linked Records
              </p>
              <LinkedRecordsTable
                records={detail.linked_records ?? []}
                provenance={detail.attribute_provenance}
              />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-primary)] mb-2">
                Source → Golden
              </p>
              <LineageMiniGraph nodes={detail.lineage} clusterId={detail.id} />
            </div>
          </div>
        </Section>

        {/* Change timeline — collapsed by default */}
        <Section
          title="Change History"
          icon={<Clock className="w-4 h-4 text-[var(--text-primary)]" />}
          defaultOpen={false}
          count={detail.history.length}
        >
          <MdmChangeTimeline entries={detail.history} />
        </Section>
      </div>

      {/* Unmerge dialog */}
      {showUnmerge && (
        <UnmergeDialog
          clusterId={detail.id}
          // Only mark success here — don't close the dialog. UnmergeDialog
          // awaits this promise before setting its own `success` state to
          // show the "Cluster Unmerged" confirmation; closing (setShowUnmerge
          // false) inside this same .then() would unmount the dialog before
          // that state update ever rendered, so the confirmation (and its
          // own "Close" button) could never actually be seen.
          onConfirm={() =>
            unmergeCluster(detail.id).then(() => {
              setUnmergeSucceeded(true);
            })
          }
          // Fires for both "Cancel" (pre-confirm) and "Close" (post-success)
          // — only refresh the list/clear selection if an unmerge actually
          // happened; a plain cancel shouldn't touch anything.
          onCancel={() => {
            setShowUnmerge(false);
            if (unmergeSucceeded) {
              setUnmergeSucceeded(false);
              onUnmerged?.();
            }
          }}
        />
      )}
    </div>
  );
}

// ─── Result Card (left pane) ──────────────────────────────────────────────────

function ClusterCard({
  cluster,
  selected,
  onSelect,
}: {
  cluster: MdmCluster;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      data-testid="mdm-golden-record-cluster"
      className={[
        "w-full text-left px-4 py-3.5 rounded-xl border transition-all duration-150",
        selected
          ? "bg-[#86bc25]/8 border-[#86bc25]/30 shadow-[0_0_20px_rgba(134,188,37,0.06)]"
          : "bg-white/[0.02] border-[var(--border-subtle)] hover:bg-white/[0.04] hover:border-[var(--border-muted)]",
      ].join(" ")}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        {cluster.entity_type && <EntityBadge type={cluster.entity_type} />}
        <MergeBadge
          band={cluster.origin_band}
          sourceCount={cluster.source_count ?? 1}
        />
      </div>

      {(() => {
        const displayName = extractDisplayName(cluster.attributes);
        return (
          <>
            {displayName && (
              <p
                className="text-sm font-medium text-white/85 truncate mb-0.5"
                title={displayName}
              >
                {displayName}
              </p>
            )}
            <p
              className="text-[11px] font-mono text-[var(--text-primary)] truncate mb-2"
              title={cluster.id}
            >
              {toShortId(cluster.id)}
            </p>
          </>
        );
      })()}

      <div className="flex items-center gap-3 text-[11px] text-[var(--text-primary)]">
        <span className="flex items-center gap-1">
          <Layers className="w-3 h-3" />
          {cluster.source_count} sources
        </span>
        <span>·</span>
        <span>{fmtDate(cluster.last_updated)}</span>
      </div>
    </button>
  );
}

// ─── Quick Match Modal ────────────────────────────────────────────────────────

type QuickMatchStep = "input" | "running" | "results";

interface QuickMatchResult {
  entity_index: number;
  cluster_id: string;
  confidence: number;
  band: ConfidenceBand;
  top_attributes: Record<string, string>;
}

interface QuickMatchModalProps {
  runId: string;
  projectId: string;
  entityType: EntityType | null;
  onClose: () => void;
  onSelectCluster: (clusterId: string) => void;
}

function QuickMatchModal({
  runId,
  projectId,
  entityType,
  onClose,
  onSelectCluster,
}: QuickMatchModalProps) {
  const [step, setStep] = useState<QuickMatchStep>("input");
  const [fields, setFields] = useState<MdmFieldMapping[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(true);

  // Each entity is a map of field → value; start with one empty entity
  const emptyEntity = () => ({}) as Record<string, string>;
  const [entities, setEntities] = useState<Record<string, string>[]>([
    emptyEntity(),
  ]);

  const [results, setResults] = useState<QuickMatchResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState("Initialising…");

  // Load known fields for this run
  useEffect(() => {
    getFields(runId)
      .then((f) => setFields(f))
      .catch(() => {
        /* non-fatal — fall back to free-form entry */
      })
      .finally(() => setFieldsLoading(false));
  }, [runId]);

  const knownFields = fields.map((f) => f.canonical_field);

  const setEntityField = (eIdx: number, field: string, value: string) => {
    setEntities((prev) => {
      const next = [...prev];
      next[eIdx] = { ...next[eIdx], [field]: value };
      return next;
    });
  };

  const addEntity = () => setEntities((prev) => [...prev, emptyEntity()]);
  const removeEntity = (idx: number) =>
    setEntities((prev) => prev.filter((_, i) => i !== idx));

  const handleRun = async () => {
    if (!entityType) {
      setStep("input");
      setError(
        "This run has no entity type yet. Set an entity type in run configuration before quick match.",
      );
      return;
    }
    setStep("running");
    setError(null);
    setStatusMsg("Submitting entities to match engine…");
    try {
      setStatusMsg("Running match against existing golden records…");
      const resp = await quickMatchEntities(
        projectId,
        runId,
        entityType,
        entities,
      );
      setResults(resp.matches);
      setStep("results");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Quick match failed.");
      setStep("input");
    }
  };

  const hasInput = entities.some((e) => Object.values(e).some((v) => v.trim()));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        background: "rgba(10,10,18,0.88)",
        backdropFilter: "blur(10px)",
      }}
    >
      <div
        className="w-full max-w-2xl rounded-xl border shadow-2xl flex flex-col overflow-hidden"
        style={{
          background: "rgba(18,16,28,0.98)",
          border: "1px solid rgba(134,188,37,0.2)",
          maxHeight: "80vh",
        }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-[var(--border-muted)] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{
                background: "rgba(134,188,37,0.12)",
                border: "1px solid rgba(134,188,37,0.2)",
              }}
            >
              <Zap className="w-4 h-4" style={{ color: "#86BC25" }} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-[var(--text-primary)]">
                Match New Entity
              </h3>
              <p className="text-[11px] text-[var(--text-primary)]">
                Match against existing golden records using saved rules
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--text-primary)] hover:text-white/60 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Step: Input */}
        {step === "input" && (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 custom-scrollbar">
              {fieldsLoading && (
                <div className="flex items-center gap-2 text-xs text-[var(--text-primary)]">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Loading field schema…
                </div>
              )}

              {error && (
                <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
                  {error}
                </div>
              )}

              {entities.map((entity, eIdx) => (
                <div
                  key={eIdx}
                  className="rounded-lg border border-[var(--border-muted)] bg-white/[0.02] overflow-hidden"
                >
                  <div className="px-4 py-2.5 flex items-center justify-between border-b border-[var(--border-subtle)] bg-white/[0.015]">
                    <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                      Entity {eIdx + 1}
                    </span>
                    {entities.length > 1 && (
                      <button
                        onClick={() => removeEntity(eIdx)}
                        className="text-[var(--text-primary)] hover:text-red-400/70 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="px-4 py-3 grid grid-cols-2 gap-3">
                    {(knownFields.length > 0
                      ? knownFields
                      : ["name", "address", "dob", "ssn_last4"]
                    ).map((field) => (
                      <div key={field}>
                        <label className="block text-[11px] font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-1">
                          {field.replace(/_/g, " ")}
                        </label>
                        <input
                          type={
                            field.includes("ssn") || field.includes("dob")
                              ? "text"
                              : "text"
                          }
                          value={entity[field] ?? ""}
                          onChange={(e) =>
                            setEntityField(eIdx, field, e.target.value)
                          }
                          placeholder={`Enter ${field.replace(/_/g, " ")}…`}
                          className="w-full px-3 py-1.5 rounded-lg bg-[var(--overlay-subtle)] border border-[var(--border-muted)] text-sm text-[var(--text-primary)] placeholder-white/15 focus:outline-none focus:border-[rgba(134,188,37,0.35)] focus:ring-1 focus:ring-[rgba(134,188,37,0.1)] transition-colors"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <button
                onClick={addEntity}
                className="w-full py-2.5 rounded-lg border border-dashed border-[var(--border-muted)] text-xs text-[var(--text-primary)] hover:text-white/50 hover:border-[var(--border-strong)] transition-colors flex items-center justify-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" />
                Add another entity
              </button>
            </div>

            <div className="px-6 py-4 border-t border-[var(--border-muted)] flex items-center justify-end gap-3 shrink-0">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-[var(--overlay-subtle)] hover:bg-[var(--overlay-strong)] text-sm text-white/50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRun}
                disabled={!hasInput}
                className="px-5 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                style={{
                  background: hasInput ? "rgba(134,188,37,0.2)" : undefined,
                  border: "1px solid rgba(134,188,37,0.35)",
                  color: "#a7d200",
                }}
              >
                <Target className="w-3.5 h-3.5" />
                Find Matches
              </button>
            </div>
          </>
        )}

        {/* Step: Running */}
        {step === "running" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-6 py-16">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{
                background: "rgba(134,188,37,0.1)",
                border: "1px solid rgba(134,188,37,0.2)",
              }}
            >
              <Zap
                className="w-7 h-7 animate-pulse"
                style={{ color: "#86BC25" }}
              />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-white/70 mb-1">
                {statusMsg}
              </p>
              <p className="text-xs text-[var(--text-primary)]">
                Using saved match config and golden records
              </p>
            </div>
            <div className="flex gap-1.5">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-[#86BC25]/50 animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Step: Results */}
        {step === "results" && (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3 custom-scrollbar">
              {results.length === 0 ? (
                <div className="py-12 flex flex-col items-center gap-3 text-center">
                  <div className="w-10 h-10 rounded-full bg-[var(--overlay-subtle)] flex items-center justify-center">
                    <Target className="w-5 h-5 text-[var(--text-primary)]" />
                  </div>
                  <p className="text-sm text-[var(--text-primary)]">
                    No matching golden records found
                  </p>
                  <p className="text-xs text-[var(--text-primary)]">
                    Try adjusting thresholds in the match rule builder
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-[var(--text-primary)]">
                    {results.length} match{results.length !== 1 ? "es" : ""}{" "}
                    found
                  </p>
                  {results.map((r) => {
                    const bandCfg = BAND_CONFIG[r.band];
                    const pct = Math.round(r.confidence * 100);
                    return (
                      <button
                        key={`${r.entity_index}-${r.cluster_id}`}
                        onClick={() => {
                          onSelectCluster(r.cluster_id);
                          onClose();
                        }}
                        className="w-full text-left rounded-xl border border-[var(--border-muted)] bg-white/[0.02] hover:bg-white/[0.04] hover:border-[rgba(134,188,37,0.2)] transition-all px-4 py-3.5 group"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-semibold text-[var(--text-primary)] uppercase">
                              Entity {r.entity_index + 1}
                            </span>
                            <span className={bandCfg.classes}>
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${bandCfg.dot}`}
                              />
                              {bandCfg.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Confidence bar */}
                            <div className="w-24 h-1.5 rounded-full bg-[var(--overlay-strong)] overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${pct}%`,
                                  background:
                                    pct >= 80
                                      ? "#86BC25"
                                      : pct >= 60
                                        ? "#f59e0b"
                                        : "#ef4444",
                                }}
                              />
                            </div>
                            <span className="text-xs font-mono text-white/50 w-9 text-right">
                              {pct}%
                            </span>
                            <ArrowRight className="w-3.5 h-3.5 text-[var(--text-primary)] group-hover:text-[#86BC25]/60 transition-colors" />
                          </div>
                        </div>
                        <p className="text-[11px] font-mono text-[var(--text-primary)] truncate mb-2">
                          {r.cluster_id}
                        </p>
                        {Object.keys(r.top_attributes).length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {Object.entries(r.top_attributes)
                              .slice(0, 4)
                              .map(([k, v]) => (
                                <span
                                  key={k}
                                  className="text-[11px] px-1.5 py-0.5 rounded bg-[var(--overlay-subtle)] text-[var(--text-primary)] font-mono"
                                >
                                  {k}: {v}
                                </span>
                              ))}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t border-[var(--border-muted)] flex items-center justify-between shrink-0">
              <button
                onClick={() => {
                  setStep("input");
                  setResults([]);
                  setError(null);
                }}
                className="text-xs text-[var(--text-primary)] hover:text-white/60 transition-colors flex items-center gap-1.5"
              >
                <ArrowRight className="w-3 h-3 rotate-180" />
                Match more entities
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-[var(--overlay-subtle)] hover:bg-[var(--overlay-strong)] text-sm text-white/50 transition-colors"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main Explorer ────────────────────────────────────────────────────────────

export interface MdmGoldenRecordExplorerViewProps {
  runId: string;
  projectId: string;
  onNewRun?: () => void;
  onNavigateToAudit?: () => void;
}

// ─── Oracle Push Dialog ─────────────────────────────────────────────────────

function OraclePushDialog({
  runId,
  onClose,
}: {
  runId: string;
  onClose: () => void;
}) {
  const [form, setForm] = useState<OracleOutputConfigRequest>({
    host: "",
    port: 1521,
    service_name: "",
    schema_name: "",
    table_name: "",
    username: "",
    password: "",
  });
  const [pushStatus, setPushStatus] =
    useState<OracleOutputConfigResponse | null>(null);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleSaveConfig = async () => {
    setSaving(true);
    setError(null);
    try {
      await configureOutputTarget(runId, form);
      const status = await getOutputConfig(runId);
      setPushStatus(status);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save config.");
    } finally {
      setSaving(false);
    }
  };

  const handlePush = async () => {
    setPushing(true);
    setError(null);
    try {
      await pushGoldenRecords(runId);
      // Poll every 3s
      pollRef.current = setInterval(async () => {
        const st = await getOutputConfig(runId);
        setPushStatus(st);
        if (st.status === "completed" || st.status === "failed") {
          clearInterval(pollRef.current!);
          setPushing(false);
        }
      }, 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Push failed.");
      setPushing(false);
    }
  };

  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
    },
    [],
  );

  const isConfigured = pushStatus?.status !== undefined;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-[var(--surface-dark)] rounded-2xl border border-[var(--border-muted)] p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-white/90">Push to Oracle</h3>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white/70"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {!isConfigured ? (
          <div className="space-y-3">
            {(
              [
                "host",
                "service_name",
                "schema_name",
                "table_name",
                "username",
              ] as const
            ).map((field) => (
              <div key={field}>
                <label className="block text-xs text-white/60 mb-1 capitalize">
                  {field.replace(/_/g, " ")}
                </label>
                <input
                  className="w-full rounded-lg px-3 py-2 text-sm bg-white/[0.07] border border-[var(--border-muted)] text-white/80 focus:outline-none focus:border-[#86bc25]/40"
                  value={form[field] as string}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, [field]: e.target.value }))
                  }
                  autoComplete="off"
                />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-white/60 mb-1">Port</label>
                <input
                  type="number"
                  className="w-full rounded-lg px-3 py-2 text-sm bg-white/[0.07] border border-[var(--border-muted)] text-white/80 focus:outline-none"
                  value={form.port}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, port: Number(e.target.value) }))
                  }
                />
              </div>
              <div>
                <label className="block text-xs text-white/60 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  className="w-full rounded-lg px-3 py-2 text-sm bg-white/[0.07] border border-[var(--border-muted)] text-white/80 focus:outline-none"
                  value={form.password}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, password: e.target.value }))
                  }
                  autoComplete="new-password"
                />
              </div>
            </div>
            {error && <p className="text-xs text-red-400">{error}</p>}
            <button
              onClick={handleSaveConfig}
              disabled={saving || !form.host || !form.service_name}
              className="w-full rounded-lg py-2 text-sm font-semibold bg-[#86bc25] text-[#0e0e18] hover:opacity-90 disabled:opacity-40 transition"
            >
              {saving ? "Saving…" : "Save Config & Enable Push"}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-lg bg-white/[0.05] border border-[var(--border-muted)] p-3 text-sm space-y-1">
              <p className="text-white/70">
                Target:{" "}
                <span className="text-white/90">{pushStatus.table_name}</span>
              </p>
              <p className="text-white/70">
                Status:{" "}
                <span
                  className={`font-semibold ${pushStatus.status === "completed" ? "text-emerald-400" : pushStatus.status === "failed" ? "text-red-400" : "text-blue-400"}`}
                >
                  {pushStatus.status}
                </span>
              </p>
              {pushStatus.status !== "configured" && (
                <p className="text-white/70">
                  Rows:{" "}
                  <span className="text-white/90">
                    {pushStatus.pushed_count.toLocaleString()} /{" "}
                    {pushStatus.total.toLocaleString()}
                  </span>
                </p>
              )}
              {pushStatus.verification_status && (
                <p className="text-white/70">
                  Verified:{" "}
                  <span className="text-white/90">
                    {pushStatus.verification_status}
                  </span>
                </p>
              )}
              {pushStatus.error_detail && (
                <p className="text-red-400 text-xs">
                  {pushStatus.error_detail}
                </p>
              )}
            </div>
            {error && <p className="text-xs text-red-400">{error}</p>}
            {pushStatus.status === "configured" && (
              <button
                onClick={handlePush}
                disabled={pushing}
                className="w-full rounded-lg py-2 text-sm font-semibold bg-[#86bc25] text-[#0e0e18] hover:opacity-90 disabled:opacity-40 transition"
              >
                {pushing ? "Pushing…" : "Push Golden Records"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Run summary bar (source → golden outcomes + pair disposition) ──────────
function RunSummaryBar({
  stats,
  entityType,
  filter,
  onFilter,
  mergedCount,
  reviewCount,
}: {
  stats: MdmRunStats | null;
  entityType: EntityType | null;
  filter: "all" | "merged" | "review";
  onFilter: (f: "all" | "merged" | "review") => void;
  mergedCount: number;
  reviewCount: number;
}) {
  const s = stats ?? {};
  const source = s.source_records_total ?? 0;
  const golden = s.golden_records ?? s.clusters_produced ?? 0;
  const dupes = s.duplicates_resolved ?? Math.max(0, source - golden);
  const merged = s.merged_clusters ?? mergedCount;
  const dupePct = source > 0 ? ((dupes / source) * 100).toFixed(1) : "0.0";

  const auto = s.auto_matched ?? 0;
  const review = s.review_pending ?? reviewCount;
  const reject = s.auto_no_match ?? 0;
  const pairs = s.candidate_pairs_generated ?? auto + review + reject;
  const total = Math.max(1, auto + review + reject);
  const fmt = (n: number) => n.toLocaleString();

  const kpi = (label: string, val: number, sub: string, accent = false) => (
    <div className="rounded-xl border border-[var(--border-muted)] bg-white/[0.03] px-4 py-3">
      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[var(--text-primary)]">
        {label}
      </div>
      <div
        className={`mt-1 text-2xl font-bold tabular-nums ${accent ? "text-[#a7d200]" : "text-white/90"}`}
      >
        {fmt(val)}
      </div>
      <div className="mt-0.5 text-[11.5px] text-[var(--text-primary)]">
        {sub}
      </div>
    </div>
  );

  const tabs: { id: "all" | "merged" | "review"; label: string; n: number }[] =
    [
      { id: "all", label: "All", n: golden },
      { id: "merged", label: "Merged", n: merged },
      { id: "review", label: "Review", n: review },
    ];

  return (
    <div className="border-b border-[var(--border-subtle)] px-5 py-4">
      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
        {kpi("Source Records", source, `1 source · ${entityType ?? "entity"}`)}
        {kpi("− Duplicates Resolved", dupes, `${dupePct}% merged away`)}
        {kpi("= Golden Records", golden, "unique resolved entities", true)}
        {kpi("…of which Merged", merged, "built from 2+ records")}
      </div>

      {/* disposition */}
      <div className="mt-3 rounded-xl border border-[var(--border-muted)] bg-white/[0.03] px-4 py-3">
        <div className="flex items-baseline justify-between">
          <span className="text-[10.5px] font-semibold uppercase tracking-wider text-[var(--text-primary)]">
            Candidate-Pair Disposition
          </span>
          <span className="text-[11.5px] text-[var(--text-primary)] tabular-nums">
            {fmt(pairs)} pairs scored → {fmt(dupes)} duplicates into{" "}
            {fmt(merged)} merged
          </span>
        </div>
        <div className="mt-2.5 flex h-2.5 overflow-hidden rounded-full">
          <span
            style={{ width: `${(auto / total) * 100}%`, background: "#2f9e44" }}
          />
          <span
            style={{
              width: `${(review / total) * 100}%`,
              background: "#d9880a",
            }}
          />
          <span
            style={{
              width: `${(reject / total) * 100}%`,
              background: "#d64545",
            }}
          />
        </div>
        <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1 text-[11.5px] text-[var(--text-primary)]">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-[#2f9e44]" /> Auto-merged{" "}
            <b className="font-bold text-white/80 tabular-nums">{fmt(auto)}</b>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-[#d9880a]" /> Manual
            review{" "}
            <b className="font-bold text-white/80 tabular-nums">
              {fmt(review)}
            </b>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-[#d64545]" /> Rejected{" "}
            <b className="font-bold text-white/80 tabular-nums">
              {fmt(reject)}
            </b>
          </span>
        </div>
      </div>

      {/* filter tabs */}
      <div className="mt-3 flex gap-1.5">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => onFilter(t.id)}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
              filter === t.id
                ? "bg-[#86bc25]/15 text-[#a7d200] ring-1 ring-[#86bc25]/30"
                : "text-[var(--text-primary)] hover:text-white/70"
            }`}
          >
            {t.label}{" "}
            <span className="tabular-nums opacity-70">{fmt(t.n)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function MdmGoldenRecordExplorerView({
  runId: initialRunId,
  projectId,
  onNewRun,
  onNavigateToAudit,
}: MdmGoldenRecordExplorerViewProps) {
  // Run selector
  const [runs, setRuns] = useState<MdmRun[]>([]);
  const [activeRunId, setActiveRunId] = useState(initialRunId);

  // Run-level outcome stats for the summary bar (source→golden, disposition).
  const [runStats, setRunStats] = useState<MdmRunStats | null>(null);
  const [runEntityType, setRunEntityType] = useState<EntityType | null>(null);
  // List filter: all golden records, only merged (multi-source), or those that
  // hit the manual-review queue.
  const [listFilter, setListFilter] = useState<"all" | "merged" | "review">(
    "all",
  );

  // Quick match
  const [showQuickMatch, setShowQuickMatch] = useState(false);
  const [showOraclePush, setShowOraclePush] = useState(false);
  const activeRun = runs.find((r) => r.id === activeRunId);

  // Search & results
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);
  const [clusters, setClusters] = useState<MdmCluster[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Detail pane
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Fetch available runs for the project
  useEffect(() => {
    listRuns(projectId)
      .then((page: import("../types").PagedResult<MdmRun>) => {
        if (page.items.length) setRuns(page.items);
      })
      .catch(() => {
        /* non-fatal */
      });
  }, [projectId]);

  // Fetch run-level outcome stats for the active run (source→golden totals +
  // pair disposition). listRuns strips stats, so this needs getRunStatus.
  useEffect(() => {
    let cancelled = false;
    getRunStatus(activeRunId)
      .then((run) => {
        if (cancelled) return;
        setRunStats(
          ((run as unknown as { stats?: MdmRunStats }).stats ??
            {}) as MdmRunStats,
        );
        setRunEntityType(
          (run as unknown as { entity_type?: EntityType }).entity_type ?? null,
        );
      })
      .catch(() => {
        if (!cancelled) setRunStats(null);
      });
    return () => {
      cancelled = true;
    };
  }, [activeRunId]);

  // Search whenever query or run changes
  const doSearch = useCallback(
    async (
      q: string,
      runId: string,
      append = false,
      cur?: string,
      mergedOnly = false,
    ) => {
      setSearchLoading(true);
      setSearchError(null);
      try {
        const result = await searchGoldenRecords(runId, q, cur, mergedOnly);
        setClusters((prev) =>
          append ? [...prev, ...result.items] : result.items,
        );
        setCursor(result.cursor);
        setHasMore(result.has_more);
        // Auto-select first on fresh search
        if (!append && result.items.length > 0 && !selectedId) {
          setSelectedId(result.items[0].id);
        }
      } catch (e) {
        setSearchError(e instanceof Error ? e.message : "Search failed.");
      } finally {
        setSearchLoading(false);
      }
    },
    [selectedId],
  );

  // Effect: re-search on query/run change
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
    setSelectedId(null);
    setClusters([]);
    setCursor(undefined);
    doSearch(
      debouncedQuery,
      activeRunId,
      false,
      undefined,
      listFilter === "merged",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, activeRunId, listFilter]);

  const loadMore = () => {
    if (hasMore && !searchLoading) {
      doSearch(
        debouncedQuery,
        activeRunId,
        true,
        cursor,
        listFilter === "merged",
      );
    }
  };

  // Loaded-page filter (client-side; authoritative merged count comes from
  // runStats.merged_clusters shown in the summary bar).
  const isMerged = (c: MdmCluster) => (c.source_count ?? 1) > 1;
  const isReview = (c: MdmCluster) => c.origin_band === "hitl";
  const filteredClusters = clusters.filter((c) =>
    listFilter === "merged"
      ? isMerged(c)
      : listFilter === "review"
        ? isReview(c)
        : true,
  );

  return (
    <div
      className="flex flex-col relative"
      style={{ minHeight: "calc(100vh - 160px)" }}
    >
      {showOraclePush && (
        <OraclePushDialog
          runId={activeRunId}
          onClose={() => setShowOraclePush(false)}
        />
      )}
      <RunSummaryBar
        stats={runStats}
        entityType={runEntityType ?? activeRun?.entity_type ?? null}
        filter={listFilter}
        onFilter={setListFilter}
        mergedCount={clusters.filter(isMerged).length}
        reviewCount={clusters.filter(isReview).length}
      />
      <div className="flex gap-0 relative">
        {/* ── Left Pane ────────────────────────────────────────────── */}
        <div
          className="flex flex-col border-r border-[var(--border-subtle)] shrink-0"
          style={{ width: 340 }}
        >
          {/* Run selector + search */}
          <div className="px-4 pt-4 pb-3 border-b border-[var(--border-subtle)] space-y-2.5 shrink-0">
            {/* Run selector */}
            <div className="relative">
              <select
                value={activeRunId}
                onChange={(e) => setActiveRunId(e.target.value)}
                className="w-full appearance-none px-3 py-2 pr-8 rounded-lg bg-[var(--overlay-subtle)] border border-[var(--border-muted)] text-sm text-white/80 focus:outline-none focus:border-[#86bc25]/40 transition-colors cursor-pointer"
                aria-label="Select MDM run"
              >
                {/* Always include the initial run even if runs list hasn't loaded */}
                {runs.length === 0 && (
                  <option value={initialRunId}>
                    Run {initialRunId.slice(0, 8)}…
                  </option>
                )}
                {runs.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({r.status})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-2.5 w-3.5 h-3.5 text-[var(--text-primary)] pointer-events-none" />
            </div>

            {/* Match New Entity button */}
            <button
              onClick={() => setShowQuickMatch(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
              style={{
                background: "rgba(134,188,37,0.1)",
                border: "1px solid rgba(134,188,37,0.25)",
                color: "#a7d200",
              }}
            >
              <Zap className="w-3.5 h-3.5" />
              Match New Entity
            </button>

            {/* Oracle Push */}
            {activeRun?.status === "completed" && (
              <button
                onClick={() => setShowOraclePush(true)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                style={{
                  background: "rgba(249,115,22,0.1)",
                  border: "1px solid rgba(249,115,22,0.25)",
                  color: "#fb923c",
                }}
              >
                <Database className="w-3.5 h-3.5" />
                Push to Oracle
              </button>
            )}

            {/* Audit Console */}
            {onNavigateToAudit && (
              <button
                onClick={onNavigateToAudit}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                style={{
                  background: "rgba(139,92,246,0.1)",
                  border: "1px solid rgba(139,92,246,0.25)",
                  color: "#a78bfa",
                }}
              >
                <Shield className="w-3.5 h-3.5" />
                Audit Console
              </button>
            )}

            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-[var(--text-primary)] pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by ID, attribute, source…"
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-[var(--overlay-subtle)] border border-[var(--border-muted)] text-sm text-[var(--text-primary)] placeholder-white/20 focus:outline-none focus:border-[#86bc25]/35 focus:ring-1 focus:ring-[#86bc25]/15 transition-colors"
              />
              {searchLoading && (
                <RefreshCw className="absolute right-2.5 top-2.5 w-3.5 h-3.5 text-[var(--text-primary)] animate-spin pointer-events-none" />
              )}
            </div>
          </div>

          {/* Results list — flows with the page instead of a boxed-in scroll region */}
          <div className="px-3 py-3 space-y-1.5">
            {searchError && (
              <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/15 text-xs text-red-400">
                {searchError}
              </div>
            )}

            {!searchLoading && clusters.length === 0 && !searchError && (
              <div className="py-16 flex flex-col items-center gap-3 text-center">
                <div className="w-10 h-10 rounded-full bg-[var(--overlay-subtle)] flex items-center justify-center">
                  <Search className="w-5 h-5 text-[var(--text-primary)]" />
                </div>
                <p className="text-sm text-[var(--text-primary)]">
                  No golden records match your search
                </p>
                {query && (
                  <button
                    onClick={() => setQuery("")}
                    className="text-xs text-[#86bc25]/50 hover:text-[#86bc25]/80 transition-colors"
                  >
                    Clear search
                  </button>
                )}
              </div>
            )}

            {filteredClusters.map((c) => (
              <ClusterCard
                key={c.id}
                cluster={c}
                selected={selectedId === c.id}
                onSelect={() => setSelectedId(c.id)}
              />
            ))}

            {hasMore && (
              <button
                onClick={loadMore}
                disabled={searchLoading}
                className="w-full py-2.5 rounded-lg border border-[var(--border-subtle)] text-xs text-[var(--text-primary)] hover:text-white/60 hover:border-[var(--border-muted)] transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5"
              >
                {searchLoading ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
                Load more
              </button>
            )}
          </div>
        </div>

        {/* ── Right Pane ───────────────────────────────────────────── */}
        <div className="flex-1 min-w-0">
          {selectedId ? (
            <MdmGoldenRecordDetailPanel
              key={selectedId}
              clusterId={selectedId}
              onUnmerged={() => {
                setSelectedId(null);
                doSearch(debouncedQuery, activeRunId);
              }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-4 text-center px-8 py-24">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{
                  background: "rgba(134,188,37,0.07)",
                  border: "1px solid rgba(134,188,37,0.15)",
                }}
              >
                <Database className="w-7 h-7 text-[#86bc25]/40" />
              </div>
              <div>
                <p className="text-base font-medium text-[var(--text-primary)] mb-1">
                  No record selected
                </p>
                <p className="text-sm text-[var(--text-primary)]">
                  Select a golden record from the list to inspect its
                  attributes, sources, and history.
                </p>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={() => setShowQuickMatch(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all"
                  style={{
                    background: "rgba(134,188,37,0.08)",
                    border: "1px solid rgba(134,188,37,0.2)",
                    color: "#86bc25",
                  }}
                >
                  <Zap className="w-3.5 h-3.5" />
                  Match a new entity
                </button>
                {onNewRun && (
                  <button
                    onClick={onNewRun}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all bg-[var(--overlay-subtle)] hover:bg-[var(--overlay-strong)] border border-[var(--border-muted)] text-[var(--text-primary)] hover:text-white/60"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    New run
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Match Modal */}
      {showQuickMatch && (
        <QuickMatchModal
          runId={activeRunId}
          projectId={projectId}
          entityType={runEntityType ?? activeRun?.entity_type ?? null}
          onClose={() => setShowQuickMatch(false)}
          onSelectCluster={(id) => {
            setSelectedId(id);
            setShowQuickMatch(false);
          }}
        />
      )}
    </div>
  );
}
